import type { PercentCrop } from 'react-image-crop';

/**
 * Generates a center-based crop with the given aspect ratio
 * @param imageWidth Natural width of the image in pixels
 * @param imageHeight Natural height of the image in pixels
 * @param targetAspect Target aspect ratio (height/width)
 * @returns Crop coordinates as percentages
 */
export function generateCenterCrop(
  imageWidth: number,
  imageHeight: number,
  targetAspect: number
): PercentCrop {
  const imageAspect = imageHeight / imageWidth;

  let cropWidth: number;
  let cropHeight: number;

  if (imageAspect > targetAspect) {
    // Image is taller than target ratio - constrain by width
    cropWidth = 100;
    cropHeight = (targetAspect * imageWidth * 100) / imageHeight;
  } else {
    // Image is wider than target ratio - constrain by height
    cropHeight = 100;
    cropWidth = (imageHeight * 100) / (targetAspect * imageWidth);
  }

  // Center the crop
  const x = (100 - cropWidth) / 2;
  const y = (100 - cropHeight) / 2;

  return {
    unit: '%',
    x,
    y,
    width: cropWidth,
    height: cropHeight,
  };
}

/**
 * Determines if the aspect ratio should be rotated based on image orientation
 * @param imageWidth Natural width of the image
 * @param imageHeight Natural height of the image
 * @param puzzleAspect Initial puzzle aspect ratio (height/width)
 * @returns The aspect ratio to use (may be rotated)
 */
export function determineOptimalAspect(
  imageWidth: number,
  imageHeight: number,
  puzzleAspect: number
): number {
  // Use height/width to match puzzleAspect convention
  const imageAspect = imageHeight / imageWidth;

  const currentAspectDiff = Math.abs(imageAspect - puzzleAspect);
  const rotatedAspectDiff = Math.abs(imageAspect - 1 / puzzleAspect);

  if (rotatedAspectDiff < currentAspectDiff) {
    return 1 / puzzleAspect;
  }

  return puzzleAspect;
}
